import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Select, Stack, Typography, Unstable_Grid2 as Grid
} from '@mui/material';
import { Close as CloseIcon, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import DynamicPerformanceChart from './DynamicPerformanceChart';

// Config por parámetro. `goodWhenUp` decide el color del delta: para reps/peso/distancia subir es
// mejorar (verde); para tiempo es ambiguo (más = resistencia, menos = velocidad), así que es neutro.
const PARAM_META = {
  TIME:     { label: 'Tiempo',    unit: 's',    goodWhenUp: null, format: (v) => { const m = Math.floor(v / 60); const s = Math.round(v % 60); return m > 0 ? `${m}m ${s}s` : `${s}s`; } },
  REPS:     { label: 'Reps',      unit: 'reps', goodWhenUp: true, format: (v) => `${v} reps` },
  WEIGHT:   { label: 'Peso',      unit: 'kg',   goodWhenUp: true, format: (v) => `${v} kg` },
  DISTANCE: { label: 'Distancia', unit: 'km',   goodWhenUp: true, format: (v) => `${v} km` }
};

// Agregación por parámetro (idéntica a la de la tabla/gráfica).
function aggregate(sets, paramType) {
  if (!sets?.length) return null;
  switch (paramType) {
    case 'TIME':     return sets.reduce((s, x) => s + (x.time_seconds || 0), 0);
    case 'REPS':     return sets.reduce((s, x) => s + (x.repetitions || 0), 0);
    case 'DISTANCE': return sets.reduce((s, x) => s + (x.distance_km || 0), 0);
    case 'WEIGHT': {
      const vals = sets.map((x) => x.weight_kg).filter(Boolean);
      return vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : null;
    }
    default: return null;
  }
}

const sessionLabel = (perf) => {
  try { return format(parseISO(perf.period_start_date), 'd MMM yyyy', { locale: es }); }
  catch { return perf.period_start_date; }
};

const deltaColor = (good) => {
  if (good === null) return 'text.primary';
  return good ? 'success.main' : 'error.main';
};

/**
 * Compara dos sesiones del MISMO ejercicio: reutiliza la gráfica de progreso (dos puntos, sesión A →
 * sesión B) y muestra el cambio numérico por parámetro con flecha y color (mejora en verde).
 */
function PerformanceCompareModal({ open, onClose, performances, params, exerciseName = '' }) {
  const ordered = useMemo(
    () => [...performances].sort((a, b) => new Date(a.period_start_date) - new Date(b.period_start_date)),
    [performances]
  );

  const [fromId, setFromId] = useState(ordered[0]?.id);
  const [toId, setToId] = useState(ordered[ordered.length - 1]?.id);

  const from = ordered.find((p) => String(p.id) === String(fromId)) || ordered[0];
  const to   = ordered.find((p) => String(p.id) === String(toId)) || ordered[ordered.length - 1];

  const paramTypes = params.map((p) => p.parameter_type);
  const sameSession = String(from?.id) === String(to?.id);

  const deltas = useMemo(() => {
    if (!from || !to) return [];
    return paramTypes
      .map((pt) => {
        const a = aggregate(from.sets, pt);
        const b = aggregate(to.sets, pt);
        if (a === null || b === null) return null;
        const delta = Math.round((b - a) * 10) / 10;
        const meta = PARAM_META[pt] || { label: pt, unit: '', goodWhenUp: null };
        const good = meta.goodWhenUp === null || delta === 0 ? null : (delta > 0) === meta.goodWhenUp;
        return { paramType: pt, meta, a, b, delta, good };
      })
      .filter(Boolean);
  }, [from, to, paramTypes]);

  // Las dos sesiones seleccionadas alimentan la gráfica ya existente (dos puntos A → B).
  const chartPerformances = useMemo(() => {
    if (!from || !to || sameSession) return [];
    return [from, to].sort((a, b) => new Date(a.period_start_date) - new Date(b.period_start_date));
  }, [from, to, sameSession]);

  const selectSx = { '& .MuiSelect-select': { py: 0.75 } };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.strong' } }}
    >
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          Comparar sesiones
          {exerciseName && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 500 }}>
              {exerciseName}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            <Grid xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>Sesión A</Typography>
              <Select fullWidth size="small" value={fromId} onChange={(e) => setFromId(e.target.value)} sx={selectSx}>
                {ordered.map((p) => <MenuItem key={p.id} value={p.id}>{sessionLabel(p)}</MenuItem>)}
              </Select>
            </Grid>
            <Grid xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>Sesión B</Typography>
              <Select fullWidth size="small" value={toId} onChange={(e) => setToId(e.target.value)} sx={selectSx}>
                {ordered.map((p) => <MenuItem key={p.id} value={p.id}>{sessionLabel(p)}</MenuItem>)}
              </Select>
            </Grid>
          </Grid>

          {sameSession ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 1 }}>
              Selecciona dos sesiones distintas para comparar.
            </Typography>
          ) : (
            <>
              {chartPerformances.length > 0 && (
                <DynamicPerformanceChart performances={chartPerformances} params={params} />
              )}

              {deltas.length > 0 ? (
                <Stack spacing={0.75} sx={{ backgroundColor: 'surface.raised', borderRadius: '12px', p: 1.5, border: '1px solid', borderColor: 'hairline.main' }}>
                  {deltas.map((d) => {
                    const color = deltaColor(d.good);
                    const Arrow = d.delta > 0 ? ArrowUpward : ArrowDownward;
                    return (
                      <Stack key={d.paramType} direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.meta.label}</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {d.meta.format ? `${d.meta.format(d.a)} → ${d.meta.format(d.b)}` : `${d.a} → ${d.b}`}
                          </Typography>
                          <Stack direction="row" alignItems="center" sx={{ color, minWidth: 64, justifyContent: 'flex-end' }}>
                            {d.delta !== 0 && <Arrow sx={{ fontSize: 14 }} />}
                            <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                              {d.delta > 0 ? '+' : ''}{d.delta} {d.meta.unit}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 1 }}>
                  Las dos sesiones no comparten parámetros para comparar.
                </Typography>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button color="inherit" onClick={onClose} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

PerformanceCompareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  performances: PropTypes.arrayOf(PropTypes.object).isRequired,
  params: PropTypes.arrayOf(PropTypes.shape({ parameter_type: PropTypes.string.isRequired })).isRequired,
  exerciseName: PropTypes.string
};

export default PerformanceCompareModal;
